/**
 * @file Components/Sections/Testimonial.jsx
 * @description Testimonial carousel section. Fetches customer reviews from the API
 * and displays them in a Swiper slider with ratings, names, and locations.
 */

import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, Navigation } from 'swiper/modules';
import { FaQuoteRight, FaStar, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { getAllTestimonials, createTestimonial } from '../../services/api';

const Testimonial = () => {
    const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    location: "",
    image: "",
    review: "",
  });

  const fetchTestimonials = async () => {
    try {
      setIsLoading(true);
      setError(null);
      // The API function now handles different response formats
      const data = await getAllTestimonials();
      // Ensure we always set an array, even if the response is malformed
      const safeData = Array.isArray(data) ? data : [];
      console.log('Fetched testimonials:', safeData);
      setTestimonials(safeData);
    } catch (err) {
      console.error('Error fetching testimonials:', err);
      setError('Failed to load testimonials. Please try again later.');
      setTestimonials([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/api/testimonials', formData);
      setTestimonials((prev) => [...prev, response.data]);
      setFormData({ name: "", location: "", image: "", review: "" });
      setShowForm(false);
    } catch (err) {
      console.error("Failed to submit testimonial:", err);
      // Handle error (e.g., show error message to user)
    }
  };

  const TestimonialCard = ({ testimonial }) => (
    <div className="bg-white rounded-lg shadow-lg p-6 relative transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl mx-2">
      <div className="absolute top-4 right-4">
        <FaQuoteRight className="text-[#243185] text-4xl opacity-10" />
      </div>
      <div className="flex flex-col">
        <div className="flex items-center mb-4">
          <div className="w-16 h-16 rounded-full overflow-hidden border-4 border-[#243185]/10">
            <img
              src={testimonial.image}
              alt={testimonial.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="ml-4">
            <h4 className="font-bold text-[#243185] text-lg">
              {testimonial.name}
            </h4>
            <p className="text-gray-600 text-sm">{testimonial.location}</p>
            <div className="flex items-center mt-1">
              {[...Array(5)].map((_, i) => (
                <FaStar key={i} className="text-yellow-400 text-sm" />
              ))}
            </div>
          </div>
        </div>
        <p className="text-gray-700 leading-relaxed">
          "{testimonial.review}"
        </p>
      </div>
    </div>
  );

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[#243185] mb-4">
            What Our Travelers Say
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Real experiences shared by travelers who explored India with us
          </p>
        </div>

        {/* Custom Navigation Buttons */}
        <div className="relative">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500 mb-4">{error}</p>
              <button
                onClick={fetchTestimonials}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : (
            <Swiper
              modules={[Pagination, Autoplay, Navigation]}
              spaceBetween={30}
              slidesPerView={1}
              pagination={{ clickable: true }}
              autoplay={{
                delay: 5000,
                disableOnInteraction: false,
              }}
              navigation={{
                nextEl: '.testimonial-next',
                prevEl: '.testimonial-prev',
              }}
              breakpoints={{
                640: {
                  slidesPerView: 1,
                },
                768: {
                  slidesPerView: 2,
                },
                1024: {
                  slidesPerView: 3,
                },
              }}
              className="pb-12"
            >
              {testimonials.length > 0 ? (
                testimonials.map((testimonial, index) => {
                  // Ensure each testimonial has required fields
                  const safeTestimonial = {
                    id: testimonial.id || `testimonial-${index}`,
                    name: testimonial.name || 'Anonymous',
                    review: testimonial.review || '',
                    rating: typeof testimonial.rating === 'number' ? testimonial.rating : 5,
                    location: testimonial.location || '',
                    image: testimonial.image || '/default-avatar.jpg'
                  };
                  return (
                    <SwiperSlide key={safeTestimonial.id}>
                      <TestimonialCard testimonial={safeTestimonial} />
                    </SwiperSlide>
                  );
                })
              ) : !isLoading && !error ? (
                <div className="text-center py-8 text-gray-500 col-span-full">
                  No testimonials available at the moment.
                </div>
              ) : null}
            </Swiper>
          )}
        </div>

        {/* Review Action */}
        <div className="mt-12 text-center">
          <button
            onClick={() => setShowForm((prev) => !prev)}
            className="inline-flex items-center px-6 py-3 bg-[#243185] text-white font-semibold rounded-[50px] hover:bg-blue-700 transition-colors duration-300"
          >
            {showForm ? "Close Form" : "Share Your Experience"}
          </button>
        </div>

        {/* Review Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-8 shadow-xl max-w-xl w-full">
              <h3 className="text-2xl font-bold text-[#243185] mb-6 text-center">
                Share Your Travel Story
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#243185] focus:border-[#243185] transition-colors duration-300"
                    required
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="location"
                    placeholder="Your Location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#243185] focus:border-[#243185] transition-colors duration-300"
                    required
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="image"
                    placeholder="Your Photo URL"
                    value={formData.image}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#243185] focus:border-[#243185] transition-colors duration-300"
                    required
                  />
                </div>
                <div>
                  <textarea
                    name="review"
                    placeholder="Share your experience with us..."
                    value={formData.review}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#243185] focus:border-[#243185] transition-colors duration-300"
                    rows={4}
                    required
                  />
                </div>
                <div className="flex justify-end gap-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-[50px] hover:bg-gray-200 transition-colors duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-[#243185] text-white rounded-[50px] hover:bg-blue-700 transition-colors duration-300"
                  >
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonial;
